import axios, { type AxiosResponse } from "axios";
import {
  BillingInvoiceResultSchema,
  BillingOrderResultSchema,
  BillingInvoiceItemSchema,
  BillingOrderItemSchema,
  BillingPaymentMethodItemSchema,
} from "@/schemas/index";
import type {
  BillingInvoiceResultType,
  BillingOrderResultType,
  BillingInvoiceItemType,
  BillingOrderItemType,
  BillingPaymentMethodItemType,
  AccountItemType,
  PayStandCheckoutDataType,
} from "@/schemas/index";
import env from "@/app/config/env";

export interface ServiceBillingApiType {
  fetchBillingInvoices: (
    pageIndex: number,
    pageSize: number,
    accountId?: string,
    period?: number | string,
  ) => Promise<BillingInvoiceResultType>;
  fetchBillingInvoiceById: (
    id: BillingInvoiceItemType["Number"],
  ) => Promise<BillingInvoiceItemType>;
  fetchBillingOrders: (
    pageIndex: number,
    pageSize: number,
  ) => Promise<BillingOrderResultType>;
  fetchBillingOrderById: (
    id: BillingOrderItemType["Number"],
  ) => Promise<BillingOrderItemType>;
  fetchBillingPaymentMethods: (
    id: AccountItemType["Id"],
  ) => Promise<BillingPaymentMethodItemType[]>;
  createPaymentMethod: (
    legalEntityId: string,
    data: PayStandCheckoutDataType,
  ) => Promise<void>;
  updatePaymentMethod: (
    id: BillingPaymentMethodItemType["Id"],
    data: Partial<BillingPaymentMethodItemType>,
  ) => Promise<void>;
  deletePaymentMethod: (
    id: BillingPaymentMethodItemType["Id"],
  ) => Promise<void>;
}

export const serviceBillingApi: ServiceBillingApiType = {
  fetchBillingInvoices: (pageIndex, pageSize, accountId, period) => {
    return axios
      .get(`${env.services.portal}/invoices`, {
        params: { pageIndex, pageSize, accountId, period },
      })
      .then((response: AxiosResponse<unknown>) => {
        return BillingInvoiceResultSchema.parse(response.data);
      });
  },

  fetchBillingInvoiceById: (id) => {
    return axios
      .get(`${env.services.portal}/invoices/${id}`)
      .then((response: AxiosResponse<unknown>) => {
        return BillingInvoiceItemSchema.parse(response.data);
      });
  },

  fetchBillingOrders: (pageIndex, pageSize) => {
    return axios
      .get(`${env.services.portal}/orders`, {
        params: { pageIndex, pageSize },
      })
      .then((response: AxiosResponse<unknown>) => {
        return BillingOrderResultSchema.parse(response.data);
      });
  },

  fetchBillingOrderById: (id) => {
    return axios
      .get(`${env.services.portal}/orders/${id}`)
      .then((response: AxiosResponse<unknown>) => {
        return BillingOrderItemSchema.parse(response.data);
      });
  },

  fetchBillingPaymentMethods: (id) => {
    return axios
      .get(`${env.services.portal}/paymentmethods`, {
        params: { accountId: id },
      })
      .then((response: AxiosResponse<unknown[]>) => {
        return response.data?.map((item) =>
          BillingPaymentMethodItemSchema.parse(item),
        );
      });
  },

  createPaymentMethod: (legalEntityId, data) => {
    return axios.post(
      `${env.services.portal}/paymentmethods?legalEntityId=${legalEntityId}`,
      data,
    );
  },

  updatePaymentMethod: (id, data) => {
    return axios.patch(`${env.services.portal}/paymentmethods/${id}`, data);
  },

  deletePaymentMethod: (id) => {
    return axios.delete(`${env.services.portal}/paymentmethods/${id}`);
  },
};
